const { Cart } = window.BAO
const { formatMoney, getElement, translateString } = window.BAO.Utils
const { productDataCache } = window.BAO.Caches

function warmUpCache (cache) {
  /** @param {string[]} handles */
  return function (...handles) {
    handles.forEach(handle => cache.get(handle))
  }
}

class GiftWithPurchaseProduct extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      // Functional
      'id-input',
      'quantity-input',
      'form',
      'submit',

      // Decorational
      'image',
      'title',
      'price',
    ]
  }

  connectedCallback () {
    super.connectedCallback()

    this._disabled = false

    warmUpCache(productDataCache)(...this.relatedProductHandles)

    const ruleData = this.closest('[data-rule-id]').dataset
    this.setQuantity(ruleData.ruleQuantity)
    this.setProperties({
      discount: (ruleData.ruleDiscount / 100).toFixed(2),
      quantity: ruleData.ruleQuantity,
      ruleId: ruleData.ruleId,
    })
  }

  setupListeners () {
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.handleProductSelectorChange = this.handleProductSelectorChange.bind(
      this,
    )
    this.updateVariant = this.updateVariant.bind(this)

    this.productSelectors.forEach(selector => {
      this.listeners.add(selector, 'change', this.handleProductSelectorChange)
    })

    this.variantSelectors.forEach(selector => {
      this.listeners.add(selector, 'change', this.updateVariant)
    })

    this.listeners.add(this.els.form.element, 'submit', this.handleFormSubmit)
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit (event) {
    event.preventDefault()

    this.addToCart()
  }

  async addToCart () {
    this.dispatchEvent(
      new CustomEvent('gift-with-purchase:product:before-add', {
        bubbles: true,
      }),
    )

    const formData = new FormData(this.els.form.element)

    const sectionsToRequest = (() => {
      if (getElement('push-cart')) {
        return window.BAO.sectionRenderer.pushCartSections
      }

      if (getElement('cart-items')) {
        return window.BAO.sectionRenderer.cartSections
      }

      return []
    })()

    const state = await Cart.addItemFromForm(formData, {
      sections: sectionsToRequest.map(s => s.section),
    })

    this.dispatchEvent(
      new CustomEvent('gift-with-purchase:product:after-add', { bubbles: true }),
    )

    return state
  }

  /**
   * @param {{
   *  discount: string;
   *  quantity: string;
   *  ruleId: string;
   * }} options
   */
  setProperties ({ discount, quantity, ruleId }) {
    this.els['properties.discount'].element.value = discount
    this.els['properties.quantity'].element.value = quantity
    this.els['properties.rule-id'].element.value = ruleId
  }

  setQuantity (quantity) {
    this.els['quantity-input'].element.value = quantity
  }

  /** @param {Event} event */
  handleProductSelectorChange (event) {
    /** @type {HTMLSelectElement} */
    const selector = event.target
    const selectedOption = selector.selectedOptions[0]

    this.updateProduct(selectedOption.dataset.productHandle)
  }

  async updateProduct (handle) {
    const product = await productDataCache.get(handle)
    const variant = this.getVariantFromOptions(product, this.selectedOptions)

    this.dispatchEvent(
      new CustomEvent('gift-with-purchase:product:replace', {
        bubbles: true,
        detail: {
          product,
          variant,
        },
      }),
    )
  }

  updateVariant () {
    this.render()
  }

  async render () {
    const variant = this.variantFromOptionSelectors

    this.els.price.element.textContent = translateString(
      this.translations.price,
      {
        price: formatMoney(variant.price),
      },
    )

    this.els['id-input'].element.value = variant.id

    this.els.submit.element.disabled = !variant.available
    this.els['submit-text'].element.textContent = variant.available
      ? this.translations.add_to_bag
      : this.translations.out_of_stock
  }

  /**
   * @param {{ variants: { option1?: string; option2?: string; option3?: string; }[] }} product
   * @param {{ position: string; value: string; }[]} options
   */
  getVariantFromOptions (product, options) {
    return product.variants.find(variant => {
      return options.every(
        option => variant[`option${option.position}`] === option.value,
      )
    })
  }

  /** @returns {string[]} */
  get relatedProductHandles () {
    return JSON.parse(this.getAttribute('related-product-handles'))
  }

  get product () {
    return JSON.parse(this.getAttribute('product'))
  }

  /** @returns {Record<'add_to_bag' | 'out_of_stock' | 'price', string>} */
  get translations () {
    return JSON.parse(this.getAttribute('translations'))
  }

  /** @returns {HTMLSelectElement[]} */
  get optionSelectors () {
    const { 'option-selector': optionSelector } = this.els

    if (!optionSelector) {
      return []
    }

    return optionSelector.elements
  }

  get productSelectors () {
    return this.optionSelectors.filter(
      selector => selector.dataset.optionType === 'product',
    )
  }

  get variantSelectors () {
    return this.optionSelectors.filter(
      selector => selector.dataset.optionType === 'variant',
    )
  }

  get variantFromOptionSelectors () {
    return this.getVariantFromOptions(this.product, this.selectedOptions)
  }

  get selectedOptions () {
    return this.variantSelectors.map(selector => {
      return {
        position: selector.dataset.optionPosition,
        value: selector.value,
      }
    })
  }

  get disabled () {
    return this._disabled
  }

  set disabled (disabled) {
    this._disabled = disabled

    this.els.submit.element.disabled = disabled
  }
}

if (!customElements.get('gift-with-purchase-product')) {
  customElements.define('gift-with-purchase-product', GiftWithPurchaseProduct)
}
